import { TrendingUp, GraduationCap } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface XPBarWidgetProps {
  level: number | null;
  currentXP?: number | null;
  xpToNext?: number | null;
  xp?: number | null;
  playerClass?: string | null;
  abilities?: string[];
  trophyCombat?: number | null;
  trophyMagic?: number | null;
  compact?: boolean;
}

export function XPBarWidget({
  level, currentXP, xpToNext, xp, playerClass,
  abilities, trophyCombat, trophyMagic, compact,
}: XPBarWidgetProps) {
  const lvl = level ?? 1;
  const current = currentXP ?? xp ?? 0;
  const next = xpToNext ?? 100;
  const pct = next > 0 ? Math.min(100, Math.round((current / next) * 100)) : 0;

  if (compact) {
    return (
      <div className="flex items-center gap-1 text-muted-foreground">
        <TrendingUp className="w-3 h-3 text-primary" />
        <span className="text-foreground font-medium">Lv{lvl}</span>
        {playerClass && <span className="text-[10px] opacity-60">{playerClass}</span>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-primary" />
          <span className="text-lg font-bold text-foreground">Level {lvl}</span>
          {playerClass && (
            <span className="text-sm text-muted-foreground">({playerClass})</span>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Experience</span>
          <span>{current} / {next} XP</span>
        </div>
        <Progress value={pct} className="h-2.5" />
      </div>

      {abilities && abilities.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Abilities</div>
          <div className="flex flex-wrap gap-1">
            {abilities.map((a: string) => (
              <span key={a} className="text-xs bg-primary/10 text-primary rounded-md px-2 py-0.5">{a}</span>
            ))}
          </div>
        </div>
      )}

      {(trophyCombat != null || trophyMagic != null) && (
        <div className="flex gap-3 text-xs text-muted-foreground">
          {trophyCombat != null && <span>⚔️ Combat trophies: {trophyCombat}</span>}
          {trophyMagic != null && <span>✨ Magic trophies: {trophyMagic}</span>}
        </div>
      )}
    </div>
  );
}

// Alias for character_classes XP status bar
export function XPStatus(props: XPBarWidgetProps) {
  return <XPBarWidget {...props} compact />;
}
